import { useEffect, useState } from "react";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";
import { ProductCard } from "./ProductCard";
import { Skeleton } from "@/components/ui/skeleton";
import { ProductListItem, productsApi } from "@/services/productsApi";

interface ProductCarouselProps {
  title: string;
  description?: string;
  type: "top-sold" | "newest";
  limit?: number;
}

export const ProductCarousel = ({ title, description, type, limit = 8 }: ProductCarouselProps) => {
  const [products, setProducts] = useState<ProductListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const response = type === "top-sold" 
          ? await productsApi.getTopSoldProducts(limit)
          : await productsApi.getNewestProducts(limit);
        
        setProducts(response.items);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Erreur lors du chargement des produits");
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, [type, limit]);

  if (error) {
    return (
      <section className="container mx-auto px-4 py-16">
        <div className="text-center">
          <h2 className="font-heading text-3xl font-semibold mb-2">{title}</h2>
          {description && (
            <p className="text-muted-foreground mb-8">{description}</p>
          )}
          <p className="text-destructive">Erreur: {error}</p>
        </div>
      </section>
    );
  }

  return (
    <section className="container mx-auto px-4 py-16">
      <header className="mb-8 text-center">
        <h2 className="font-heading text-3xl font-semibold">{title}</h2>
        {description && (
          <p className="mt-2 text-muted-foreground">{description}</p>
        )}
      </header>

      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
          {Array.from({ length: limit }).map((_, i) => (
            <div key={i} className="space-y-4">
              <Skeleton className="aspect-square w-full" />
              <Skeleton className="h-4 w-3/4" />
              <Skeleton className="h-4 w-1/2" />
              <Skeleton className="h-8 w-full" />
            </div>
          ))}
        </div>
      ) : products.length > 0 ? (
        <Carousel
          opts={{
            align: "start",
            loop: false,
          }}
          className="w-full"
        >
          <CarouselContent className="-ml-2 md:-ml-4">
            {products.map((product) => (
              <CarouselItem
                key={product.id}
                className="pl-2 md:pl-4 basis-full sm:basis-1/2 md:basis-1/3 lg:basis-1/4"
              >
                <ProductCard product={product} />
              </CarouselItem>
            ))}
          </CarouselContent>
          <CarouselPrevious className="hidden md:flex" />
          <CarouselNext className="hidden md:flex" />
        </Carousel>
      ) : (
        <p className="text-center text-muted-foreground">Aucun produit disponible</p>
      )}
    </section>
  );
};